import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { 
  Truck, 
  RotateCcw, 
  Info, 
  ShieldCheck, 
  Lock, 
  Headphones, 
  CheckCircle2, 
  Clock, 
  CreditCard, 
  FileText,
  MapPin,
  Sparkles
} from "lucide-react";

export const POLICY_CONTENTS = {
  shipping: {
    title: "Shipping & Delivery Policy",
    subtitle: "Fast, insured pan-India delivery for retail & wholesale jewellery.",
    icon: Truck,
    badge: "Express Insured Dispatch",
    color: "from-amber-600 to-amber-800",
    sections: [
      {
        heading: "1. Dispatch & Processing Timeline",
        text: "All orders are securely packaged and dispatched within 24 to 48 hours of confirmation. For wholesale bulk orders, customized finishing may take up to 2-3 business days.",
      },
      {
        heading: "2. Pan-India Delivery Timelines",
        text: "Standard transit timeline is 3 to 5 business days across major metros and Tier 1/2 cities. Remote regions may take 5 to 7 days.",
      },
      {
        heading: "3. 100% Transit Insurance",
        text: "Every parcel shipped by Zaveri Trunk is fully insured against theft, loss, or transit damage. We partner with India's top courier services (BlueDart, Delhivery, DTDC).",
      },
      {
        heading: "4. Real-time Live Tracking",
        text: "As soon as your shipment is dispatched, automated SMS and WhatsApp notifications with live tracking links and AWB details will be sent directly to your registered number.",
      },
      {
        heading: "5. Shipping Charges",
        text: "We offer free standard shipping on eligible orders. Wholesale bulk cargo and surface transport freight are transparently calculated at checkout based on package weight.",
      }
    ]
  },
  return: {
    title: "Return & Refund Policy",
    subtitle: "100% hassle-free returns, replacements, and quick refunds.",
    icon: RotateCcw,
    badge: "Guaranteed Buyer Protection",
    color: "from-emerald-700 to-teal-900",
    sections: [
      {
        heading: "1. 7-Day Easy Return Window",
        text: "If you receive any damaged, defective, or incorrect piece, you can raise a return or exchange request within 7 days of receiving your order.",
      },
      {
        heading: "2. 100% Full Refund Guarantee",
        text: "For prepaid orders cancelled or returned within the policy, the complete amount is automatically credited back to your original payment source (UPI/Card/Bank) in 3-5 business days.",
      },
      {
        heading: "3. Cash on Delivery (COD) Advance Amount Policy",
        text: "If a COD order is cancelled prior to dispatch, any advance amount paid is immediately initiated for refund to your provided UPI ID or bank account.",
      },
      {
        heading: "4. Condition for Return",
        text: "Jewellery items must be unused, in their original tamper-evident packaging with tags intact. Please record a brief unboxing video upon parcel delivery for expedited processing.",
      },
      {
        heading: "5. Quick WhatsApp Support Desk",
        text: "Our dedicated support team is available on WhatsApp and Phone (+91 90824 44611) to instantly assist with order tracking, returns, and replacements.",
      }
    ]
  },
  about: {
    title: "About Zaveri Trunk",
    subtitle: "India's premier destination for exquisite fashion, bridal & wholesale jewellery.",
    icon: Sparkles,
    badge: "Heritage Craftsmanship",
    color: "from-[#3a2a0e] to-[#6b4d16]",
    sections: [
      {
        heading: "1. The Zaveri Trunk Legacy",
        text: "Zaveri Trunk represents the pinnacle of Indian traditional and contemporary jewellery craftsmanship. We bridge master artisans and manufacturers directly with retailers and discerning buyers across India.",
      },
      {
        heading: "2. Factory Direct Wholesale & Retail",
        text: "Enjoy manufacturer pricing on AD/CZ, Temple Jewellery, Casting Sets, Oxidised Collections, Anti-Tarnish Chains, and Bridal Sets without intermediate markups.",
      },
      {
        heading: "3. Certified Quality & Purity",
        text: "Every piece passes through a rigorous 3-tier quality inspection ensuring superior plating durability, skin-safe hypoallergenic finishes, and precision stone-setting.",
      },
      {
        heading: "4. Custom Jewellery Manufacturing",
        text: "We specialize in tailored designs, bridal customization, and volume bulk production for boutique owners, jewellers, and retailers across India.",
      }
    ]
  },
  terms: {
    title: "Terms & Conditions",
    subtitle: "Transparent commercial terms, pricing integrity, and order policies.",
    icon: FileText,
    badge: "Official Terms",
    color: "from-stone-800 to-stone-950",
    sections: [
      {
        heading: "1. Acceptance of Terms",
        text: "By accessing or placing orders on Zaveri Trunk, you agree to comply with our platform policies, catalog pricing, and commercial terms.",
      },
      {
        heading: "2. Pricing & GST Billing",
        text: "All wholesale catalogues offer verified GST invoices. Prices are subject to gold/brass commodity variations and are locked once an order is placed.",
      },
      {
        heading: "3. Intellectual Property",
        text: "All product photographs, designs, trademarks, and multimedia assets are proprietary to Zaveri Trunk and protected under copyright laws.",
      }
    ]
  },
  privacy: {
    title: "Privacy & Data Protection",
    subtitle: "Your privacy and payment security are our top priorities.",
    icon: Lock,
    badge: "",
    color: "from-blue-900 to-indigo-950",
    sections: [
      {
        heading: "1. Information Security",
        text: "We do not store your raw banking or card details. All transactions are securely processed through RBI-authorized payment gateways.",
      },
      {
        heading: "2. Privacy Commitment",
        text: "Your contact details and order history are kept strictly confidential and never shared or sold to third-party marketing brokers.",
      }
    ]
  }
};

export const FooterPolicyModal = ({ open, onOpenChange, policyKey, linkData }) => {
  const defaultPolicy = POLICY_CONTENTS[policyKey] || POLICY_CONTENTS.shipping;
  const IconComponent = defaultPolicy.icon || Info;

  const title = linkData?.page_title || defaultPolicy.title;
  const subtitle = linkData?.page_subtitle !== undefined ? linkData.page_subtitle : defaultPolicy.subtitle;
  const badge = linkData?.badge !== undefined ? linkData.badge : defaultPolicy.badge;
  const color = linkData?.theme_color || defaultPolicy.color;
  
  let sections = defaultPolicy.sections;
  if (linkData?.sections && Array.isArray(linkData.sections) && linkData.sections.length > 0) {
    sections = linkData.sections;
  } else if (linkData?.content && typeof linkData.content === "string" && linkData.content.trim()) {
    sections = [{ heading: linkData.page_title || "Information", text: linkData.content }];
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 bg-white border border-stone-200 shadow-2xl rounded-2xl sm:rounded-3xl flex flex-col max-h-[88vh] overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${color} text-white p-5 sm:p-6 relative overflow-hidden shrink-0`}>
          <div className="flex items-center gap-3 relative z-10">
            <div className="w-10 h-10 rounded-2xl bg-white/10 text-white border border-white/20 flex items-center justify-center shrink-0 shadow-inner">
              <IconComponent className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              {badge && (
                <span className="text-[10px] uppercase font-bold tracking-widest text-amber-300/90 block">
                  {badge}
                </span>
              )}
              <DialogTitle className="font-serif-display text-xl sm:text-2xl font-bold text-white leading-tight">
                {title}
              </DialogTitle>
            </div>
          </div>
          {subtitle && (
            <DialogDescription className="text-white/80 text-xs mt-2 relative z-10">
              {subtitle}
            </DialogDescription>
          )}
        </div>

        {/* Scrollable Body */}
        <div className="p-5 sm:p-6 space-y-4 flex-1 min-h-0 overflow-y-auto text-xs text-stone-700 leading-relaxed">
          {sections.map((sec, idx) => (
            <div key={idx} className="p-3.5 bg-stone-50 rounded-xl border border-stone-200/80 space-y-1">
              <p className="font-bold text-stone-900 text-[13px] flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                {sec.heading}
              </p>
              <p className="text-stone-600 pl-5 leading-relaxed">{sec.text}</p>
            </div>
          ))}

          {/* Quick Help Box */}
          <div className="p-3.5 rounded-xl bg-amber-50/70 border border-amber-200 flex items-center justify-between gap-3 mt-4">
            <div className="flex items-center gap-2.5">
              <Headphones className="w-4 h-4 text-amber-800 shrink-0" />
              <div>
                <p className="font-semibold text-amber-950 text-xs">Have questions about this policy?</p>
                <p className="text-amber-800/80 text-[11px]">Talk to our support team on WhatsApp anytime.</p>
              </div>
            </div>
            <a
              href="https://wa.me/919082444611?text=Hi%20Zaveri%20Trunk,%20I%20have%20a%20query%20regarding%20orders%20and%20policies."
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-stone-50 border-t border-stone-200 flex items-center justify-end shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full text-xs font-semibold px-6 border-stone-300 hover:bg-stone-100 text-stone-800"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
